import common from "@charmdeer/common";
import { storageLocal } from "@pureadmin/utils";
import { get, set } from "lodash-es";
import { defineStore } from "pinia";
import { useRoute } from "vue-router";

import { accountLogout } from "@/api/account";
import { getTokenData, getUserLevel, toggleI18n } from "@/api/biz/user";
import { getUser, loginByToken, updateUserToggles } from "@/api/user";
import { updateLocale } from "@/plugins/i18n";
import { router } from "@/router";
import { store } from "@/store";
import { removeToken, setToken } from "@/utils/auth";
import { updateArmsUser } from "@/utils/log";

import { initTheme } from "../../distributions/consumer/y/theme/index";
import { useChannelStoreHook } from "./channel";
import { useHttpStoreHook } from "./http";
export const useUserStore = defineStore({
  id: "sys-user",
  state: () => ({
    user: null,
    userLevel: 0,
    capabilities: null,
    tokenData: null,
    language: "zh",
    currency: "CNY"
  }),
  actions: {
    async setUserLevel() {
      const userLevelRes = await getUserLevel();
      if (userLevelRes.success) {
        const levels = userLevelRes.levels;
        let _level = 0;
        const _userLevel = levels || [];
        if (_userLevel.length > 0) {
          if (_userLevel[0]) {
            _level = _userLevel[0].key;
          } else {
            _level = 1;
          }
        }
        this.userLevel = _level;
      }
    },
    /**
     * 同步用户的语言和货币偏好设置。
     * @param {object} [options] - 可选的配置项.
     * @param {string} [options.defaultCurrency] - 一个临时的、最高优先级的货币默认值 (会覆盖渠道默认值).
     * @param {Object} options.channelConfig - 当前渠道的静态配置 (local configs)
     * @param {boolean} options.isEnforcedLocale - 是否强制使用环境语言 (对应 Hotelux 逻辑)
     * @param {string} options.enforcedDefaultLanguage - 强制环境下的默认语言 (例如 'en' 或 'ja')
     */
    async syncPreferences(options = {}) {
      const {
        defaultCurrency = null,
        channelConfig = null,
        // isEnforcedLocale = false, // 是否是 Hotelux 这种强环境
        enforcedDefaultLanguage = "en" // 强环境下的默认值
      } = options;
      const channelStore = useChannelStoreHook();
      const apiChannelData = channelStore.channel;
      const storageLang = storageLocal().getItem("y-locale");

      let userLang, userCurr, finalLang, finalCurr;
      if (this.user) {
        const rawLang = get(this.user, "featureToggles.i18n.language");
        userLang = rawLang === "zh-TW" ? "zh_TW" : rawLang;
        userCurr = get(this.user, "featureToggles.i18n.currency");
      }
      if (userCurr) {
        finalCurr = userCurr;
      } else if (defaultCurrency) {
        finalCurr = defaultCurrency;
      } else if (apiChannelData?.currency) {
        finalCurr = apiChannelData.currency;
      } else if (channelConfig?.currency) {
        finalCurr = channelConfig.currency;
      } else {
        finalCurr = "CNY";
      }

      finalLang = "zh";

      let validStorageLang = storageLang;
      if (channelConfig?.languages && validStorageLang) {
        const isSupported = channelConfig.languages.some(
          item => item.value === validStorageLang
        );
        if (!isSupported) validStorageLang = null;
      }

      finalLang = userLang || validStorageLang || enforcedDefaultLanguage;
      await this.changeCurrency(finalCurr);
      await this.changeLanguage(finalLang);
      return {
        language: finalLang,
        currency: finalCurr
      };
    },
    removeUserLocalPreferences() {
      storageLocal().removeItem("y-locale");
    },
    async SET_USER(user) {
      const channelStore = useChannelStoreHook();
      const route = useRoute();
      //  做一个兼容（v-app）
      if (get(user, "featureToggles.i18n.language") === "zh-TW") {
        set(user, "featureToggles.i18n.language", "zh_TW");
      }
      this.user = user;
      setToken(user);
      const data = await getTokenData();
      this.tokenData = data;
      if (user.channelRef) {
        await channelStore.getChannelByKey(route?.query?.token);
      }
      this.setUserLevel();
      updateArmsUser(this.user);
    },
    SET_CAPABILITIES(capabilities) {
      this.capabilities = capabilities;
    },
    SET_CURRENCY(currency) {
      this.currency = currency;
    },
    async getUser() {
      const container = new URLSearchParams(window.location.href).get(
        "container"
      );
      const token = new URLSearchParams(window.location.href).get("token");
      const channelKey = new URLSearchParams(window.location.href).get(
        "channelKey"
      );
      if (channelKey) {
        localStorage.setItem("sys-channel", channelKey);
      }
      let res = null;
      if (container && container === "vapp" && token) {
        res = await loginByToken({
          token,
          channelKey
        });
      } else {
        res = await getUser();
      }
      if (res.user) {
        await this.SET_USER(res.user);
        await this.SET_CAPABILITIES(res.capabilities);
        return res.user;
      }
      return null;
    },
    /** 前端登出（不调用接口） */
    async logout(clearChannel = false) {
      const route = useRoute();
      const channelStore = useChannelStoreHook();
      this.user = null;
      this.capabilities = null;
      removeToken(clearChannel);
      await accountLogout();
      if (clearChannel) {
        const httpStore = useHttpStoreHook();
        httpStore.setHeaders({
          "y-platform-channel": ""
        });
        channelStore.clear();
      }
      this.syncPreferences();
      const basePath = window.location.pathname;

      const targetChannel = route?.query?.channel || channelStore.channel?.key;
      const queryStr = targetChannel ? `?channel=${targetChannel}` : "";

      const targetUrl = `${window.location.origin}${basePath}#/${queryStr}`;

      window.location.href = targetUrl;
      window.location.reload();
    },
    async changeLanguage(language) {
      this.language = language;
      await updateLocale(language);
      await storageLocal().setItem("y-locale", language);
      await initTheme();
      await this.setUserI18nLanguage(language);
    },
    async updateUserToggle(toggles) {
      const res = await updateUserToggles(toggles);
      if (res.user) {
        this.user = res.user;
      }
    },
    async setUserI18nLanguage(language) {
      if (this.user && this.user._id) {
        const i18n = get(this.user, "featureToggles.i18n", {});
        const data = {
          i18n: {
            ...i18n,
            language
          }
        };
        await toggleI18n(data);
        set(this.user, "featureToggles.i18n.language", language);
      }
    },
    async changeCurrency(currency) {
      this.currency = currency;
      await this.setUserCurrency(currency);
    },
    async setUserCurrency(currency) {
      if (this.user && this.user._id) {
        const i18n = get(this.user, "featureToggles.i18n", {});
        const data = {
          i18n: {
            ...i18n,
            currency
          }
        };
        await toggleI18n(data);
        set(this.user, "featureToggles.i18n.currency", currency);
      }
    }
  },
  getters: {
    languageISO639Style: state => {
      return common.locale.getISO639Style(state.language);
    },
    isConsumer: state => {
      return state.userLevel === 9;
    },
    preferred: state => {
      return {
        language: state.language,
        currency: state.currency,
        version: "v1"
      };
    }
  }
});

export function useUserStoreHook() {
  return useUserStore(store);
}
